import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  PlatformClinic,
  PlatformClinicsAPI,
  BackfillUserRequest,
} from "../../services/platformClinics.service";

interface Props {
  isOpen: boolean;
  clinic: PlatformClinic | null;
  onClose: () => void;
  onSuccess: () => void;
}

type Role = BackfillUserRequest["role"];
const ROLES: Role[] = ["Admin", "Doctor", "Receptionist"];

/** Explain why this clinic surfaced a "Fix User Row" action. */
function buildReason(clinic: PlatformClinic): string {
  const noAdminRow = clinic.admin_user_id == null;
  const noDoctors = clinic.doctor_count === 0;
  if (noAdminRow && noDoctors) {
    return "This clinic has no Admin users row (has_users_row = false) and shows 0 doctors.";
  }
  if (noAdminRow) {
    return "This clinic has no Admin users row (has_users_row = false).";
  }
  return "This clinic shows 0 doctors.";
}

const FixUserRowModal: React.FC<Props> = ({ isOpen, clinic, onClose, onSuccess }) => {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("Admin");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [loading, setLoading] = useState(false);

  // Prefill from the clinic's admin whenever a new clinic opens the modal.
  useEffect(() => {
    if (clinic) {
      setEmail(clinic.admin_email ?? "");
      setRole("Admin");
      setFirstName("");
      setLastName("");
    }
  }, [clinic]);

  if (!isOpen || !clinic) return null;

  const handleBackfill = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      toast.error("Email is required — enter the Cognito user's email to repair");
      return;
    }
    setLoading(true);
    try {
      const res = await PlatformClinicsAPI.backfillUser({
        email: trimmedEmail,
        clinic_id: clinic.clinic_id,
        role,
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
      });
      toast.success(
        `${res.created ? "Created" : "Repaired"} ${res.role} row for ${res.email}` +
          (res.cognito_synced ? "" : " (Cognito attrs not synced — check logs)")
      );
      onSuccess();
      onClose();
    } catch (e: any) {
      toast.error(e?.message || "Backfill failed");
    } finally {
      setLoading(false);
    }
  };

  const adminLabel = clinic.admin_name ?? clinic.admin_email ?? "no admin on record";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-5 pb-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-amber-600">
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-800 leading-tight">Fix user row</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                {clinic.name} · {adminLabel}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-slate-400 hover:text-slate-600 text-xl leading-none font-bold disabled:opacity-40"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pb-5 space-y-4">
          <p className="text-sm text-slate-600 leading-relaxed">
            {buildReason(clinic)} Running backfill creates or converges the users row from Cognito so
            staff and doctors can sign in.
          </p>

          {/* Email */}
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Cognito user email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="admin@clinic.in"
              autoFocus
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0066ff]"
            />
          </div>

          {/* Role */}
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Role</label>
            <select
              value={role}
              onChange={e => setRole(e.target.value as Role)}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#0066ff]"
            >
              {ROLES.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>

          {/* Optional names */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">First name</label>
              <input
                type="text"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                placeholder="optional"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0066ff]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Last name</label>
              <input
                type="text"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                placeholder="optional"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0066ff]"
              />
            </div>
          </div>

          {/* Endpoint that will be called — read-only chip */}
          <div className="rounded-lg bg-slate-100 border border-slate-200 px-3 py-2.5 text-sm text-slate-500 font-mono select-all">
            POST /platform/users/backfill
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-1">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm text-slate-600 rounded-xl hover:bg-slate-50 disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              onClick={handleBackfill}
              disabled={loading || !email.trim()}
              className="px-4 py-2 bg-[#2563eb] text-white text-sm font-medium rounded-xl hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? "Running…" : "Run backfill"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FixUserRowModal;
